import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { TokenRefreshService } from '../../services/token-refresh/token-refresh.service';
import {
  getIpBlockErrorCode,
  shouldSkipIpBlockRedirect,
} from '../../utils/ip-block-error.util';
import { isPublicApiUrl, isPublicFrontendPath } from '../../utils/public-route.util';

/** Public auth/signup routes where 401 is an expected user error, not session expiry. */
const AUTH_ERROR_BYPASS_URLS = [
  '/v1/auth/',
  '/v1/channel-partner/auth/',
];

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenRefreshService = inject(TokenRefreshService);
  const router = inject(Router);

  const redirectToIpBlocked = (error: HttpErrorResponse): void => {
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '';
    if (
      isPublicFrontendPath(pathname) ||
      isPublicApiUrl(req.url) ||
      shouldSkipIpBlockRedirect(pathname, req.url)
    ) {
      return;
    }

    const code = getIpBlockErrorCode(error);
    if (!code) {
      return;
    }

    void router.navigate(['/ip-blocked'], {
      queryParams: { reason: code },
      replaceUrl: true,
    });
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        redirectToIpBlocked(error);
        return throwError(() => error);
      }

      if (error.status !== 401) {
        return throwError(() => error);
      }

      if (
        AUTH_ERROR_BYPASS_URLS.some(url => req.url.includes(url)) ||
        isPublicApiUrl(req.url) ||
        isPublicFrontendPath()
      ) {
        return throwError(() => error);
      }

      return tokenRefreshService.refreshTokens(req.url).pipe(
        switchMap(newToken =>
          next(
            req.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` },
            }),
          ),
        ),
        catchError(() => throwError(() => error)),
      );
    }),
  );
};
